/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import '../../styles/NewApplication.css';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Button, Typography } from '@mui/material';
import useFormCache from '../../hooks/useFormCache';
import AddDepartmentDialog from '../admin/AddDepartmentDialog';
import { formatAccountNumber, displayAccountNumber } from '../../utils/formatUtils';

axios.defaults.withCredentials = true;

const NewApplication = ({ 
  mode = 'new', 
  user, 
  applicationId,
  onFormChange,
  onSaveConfirm,
  initialData,
  isAdminReview = false,
  hideFormHeading = false,
  onSaved
}) => {
  const navigate = useNavigate();

  // Generate a unique cache key based on user and mode
  const cacheKey = `application-form-${user?.id || 'guest'}-${mode}${applicationId ? `-${applicationId}` : ''}`;
  
  // Initialize form cache
  const { cachedData, updateCache, clearCache, isLoading: isCacheLoading, isSaving } = useFormCache(cacheKey);
  
  // Flag to prevent infinite loops during cache restoration
  const [isRestoringCache, setIsRestoringCache] = useState(false);
  const [cacheRestored, setCacheRestored] = useState(false);

  // Form data states (cacheable) - Initialize with empty values, will be populated from cache
  const [projectTitle, setProjectTitle] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [personnel, setPersonnel] = useState([]);
  const [experimentConditions, setExperimentConditions] = useState([]);
  const [experimentDetails, setExperimentDetails] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState('');
  const [projectTimeline, setProjectTimeline] = useState('');
  const [experimentalDesignSummary, setExperimentalDesignSummary] = useState('');
  const [specialRequirements, setSpecialRequirements] = useState('');
  const [additionalComments, setAdditionalComments] = useState('');
  const [usesTransgenicPlants, setUsesTransgenicPlants] = useState(false);
  const [usesPathogen, setUsesPathogen] = useState(false);
  const [buaNumber, setBuaNumber] = useState('');
  const [safetyTrainingVerified, setSafetyTrainingVerified] = useState(false);
  const [funding, setFunding] = useState([]);
  const [accountNumber, setAccountNumber] = useState('');
  const [sourceOfFunds, setSourceOfFunds] = useState('');
  const [selectedUserForAccount, setSelectedUserForAccount] = useState('');
  const [selectedAccountNumber, setSelectedAccountNumber] = useState('');
  const [isNewAccount, setIsNewAccount] = useState(false);
  const [newAccountsToSave, setNewAccountsToSave] = useState([]);
  const [isPINCSU, setIsPINCSU] = useState(false);
  const [pots, setPots] = useState([{ pot_size: '', quantity: '' }]);
  const [numberOfCarts, setNumberOfCarts] = useState('');
  const [preferredSpace, setPreferredSpace] = useState('');

  // Non-cacheable states (UI/loading states)
  const [facilities, setFacilities] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [projectRoles, setProjectRoles] = useState([]);
  const [waterOptions, setWaterOptions] = useState([]);
  const [substrateOptions, setSubstrateOptions] = useState([]);
  const [speciesOptions, setSpeciesOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUserAccountNumbers, setSelectedUserAccountNumbers] = useState([]);
  const [loadingUserAccounts, setLoadingUserAccounts] = useState(false);
  const [showAddAccountForm, setShowAddAccountForm] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [potOptions, setPotOptions] = useState([]);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  
  // Add Department Dialog state
  const [addDeptDialogOpen, setAddDeptDialogOpen] = useState(false);
  const [selectedCustomDept, setSelectedCustomDept] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [facilitiesRes, departmentsRes, rolesRes] = await Promise.all([
          axios.get('/api/facilities'),
          axios.get('/api/departments'),
          axios.get('/api/project-roles')
        ]);
        setFacilities(facilitiesRes.data);
        setDepartments(departmentsRes.data);
        setProjectRoles(rolesRes.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load necessary data. Please try again later.');
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        if (personnel.length === 0 && user && projectRoles.length > 0) {
          const piRole = projectRoles.find(r => r.role === 'PI');
          if (!piRole) {
            console.error('PI role not found in project roles');
            return;
          }
          setPersonnel([{
            firstName: user.first_name || '',
            lastName: user.last_name || '',
            email: user.email || '',
            department: user.department?._id || user.department || '',
            role: piRole._id,
            isDefault: false
          }]);
        }
      } catch (error) {
        console.error('Error setting user data:', error);
        setError('Failed to load user data');
      }
    };

    fetchUserData();
  }, [user, personnel.length, projectRoles]);

  useEffect(() => {
    if (mode === 'edit' && initialData && projectRoles.length > 0) {
      // Format personnel data correctly when setting initial state
      const formattedPersonnel = initialData.users.map(user => {
        // Handle role conversion properly
        let roleId = user.role;
        
        // If role is a populated object (from backend populate), use its _id
        if (user.role && typeof user.role === 'object' && user.role._id) {
          roleId = user.role._id;
        }
        // If role is a string (role name), find the corresponding ObjectId
        else if (typeof user.role === 'string' && user.role !== '') {
          const roleObj = projectRoles.find(r => r.role === user.role);
          roleId = roleObj ? roleObj._id : user.role;
        }
        // If role is already an ObjectId string, use it as is
        // (this handles cases where role is stored as ObjectId string)
        
        const userData = {
          firstName: user.first_name || user.firstName,
          lastName: user.last_name || user.lastName,
          email: user.email,
          role: roleId,
          isDefault: user.isDefault || false,
          existingUser: user.isExistingUser,
          is_ncsu: user.is_ncsu,
          isDeleted: user.isDeleted || false
        };

        // Handle department vs customDepartment
        if (user.customDepartment) {
          userData.department = 'not_listed';
          userData.customDepartment = user.customDepartment;
        } else if (user.department) {
          userData.department = user.department._id || user.department;
        } else {
          userData.department = '';
        }

        return userData;
      });

      setProjectTitle(initialData.project_title || '');
      setStartDate(initialData.project_start_date?.split('T')[0] || '');
      setEndDate(initialData.project_end_date?.split('T')[0] || '');
      setPersonnel(formattedPersonnel);
      setSelectedFacility(initialData.facility?._id || initialData.facility || '');
      
      // Properly set experiment conditions - convert from database format to form format
      setExperimentConditions(initialData.experiment_conditions?.map(condition => {
        // Parse photo_on time (e.g., "06:00 AM" -> {hour: "06", minute: "00", period: "AM"})
        const parseTime = (timeStr) => {
          if (!timeStr) return { hour: '01', minute: '00', period: 'AM' };
          if (timeStr === 'N/A') return { hour: 'N/A', minute: 'N/A', period: 'N/A' };
          const [time, period] = timeStr.split(' ');
          const [hour, minute] = time.split(':');
          return { hour, minute, period: period || 'AM' };
        };
        
        const photoOn = parseTime(condition.photo_on);
        const photoOff = parseTime(condition.photo_off);
        const nightInterruptOn = parseTime(condition.night_interrupt_on);
        const nightInterruptOff = parseTime(condition.night_interrupt_off);
        
        return {
          photoOnHour: photoOn.hour,
          photoOnMinute: photoOn.minute,
          photoOnPeriod: photoOn.period,
          photoOffHour: photoOff.hour,
          photoOffMinute: photoOff.minute,
          photoOffPeriod: photoOff.period,
          nightInterruptOnHour: nightInterruptOn.hour,
          nightInterruptOnMinute: nightInterruptOn.minute,
          nightInterruptOnPeriod: nightInterruptOn.period,
          nightInterruptOffHour: nightInterruptOff.hour,
          nightInterruptOffMinute: nightInterruptOff.minute,
          nightInterruptOffPeriod: nightInterruptOff.period,
          tempDay: condition.temp_day || '',
          tempNight: condition.temp_night || '',
          co2Day: condition.co2_day || '',
          co2Night: condition.co2_night || '',
          humidityDay: condition.humidity_day || '',
          humidityNight: condition.humidity_night || ''
        };
      }) || []);

      // Properly set experiment details
      setExperimentDetails(initialData.experiment_details?.map(detail => ({
        water: detail.water?._id || detail.water || '',
        substrate: detail.substrate?._id || detail.substrate || '',
        species: detail.species_not_listed ? 'not_listed' : (detail.species?._id || detail.species || ''),
        speciesNotListed: detail.species_not_listed || ''
      })) || []);

      setProjectTimeline(initialData.project_timeline || '');
      setExperimentalDesignSummary(initialData.experimental_design_summary || '');
      setSpecialRequirements(initialData.special_environmental_requirements || '');
      setAdditionalComments(initialData.additional_comments || '');
      setUsesTransgenicPlants(initialData.uses_transgenic_plants || false);
      setUsesPathogen(initialData.uses_pathogen || false);
      setBuaNumber(initialData.bua_number || '');
      setSafetyTrainingVerified(initialData.safety_training_verified || false);
      // Enhanced funding data with user information for display
      const enhancedFunding = (initialData.funding || []).map(fund => {
        // Resolve user from project team (initialData.users)
        const projectUser = fund.user_email 
          ? initialData.users.find(u => u.email === fund.user_email)
          : (fund.user_id ? initialData.users.find(u => (u.user?._id || u.user)?.toString() === fund.user_id?.toString()) : null);
        const userName = fund.user_name || (projectUser ? `${projectUser.first_name || projectUser.firstName} ${projectUser.last_name || projectUser.lastName}` : null);
        const userEmail = fund.user_email || (projectUser ? projectUser.email : null);
        
        if (userName || userEmail) {
          return { ...fund, user_name: userName, user_email: userEmail };
        }
        
        // Legacy: no user info stored - show unknown, do NOT default to first user
        return { ...fund, user_name: 'Unknown User', user_email: null };
      });
      setFunding(enhancedFunding);
      // Handle both old single pot format and new multiple pots format
      if (initialData.pots && initialData.pots.length > 0) {
        // New format with multiple pots
        setPots(initialData.pots.map(pot => ({
          pot_size: pot.pot_size?._id || pot.pot_size || '',
          quantity: pot.quantity || ''
        })));
      } else if (initialData.number_of_pots || initialData.pot_size) {
        // Legacy format - convert to new format
        setPots([{
          pot_size: initialData.pot_size?._id || initialData.pot_size || '',
          quantity: initialData.number_of_pots || ''
        }]);
      } else {
        // No pot data
        setPots([{ pot_size: '', quantity: '' }]);
      }
      setNumberOfCarts(initialData.number_of_carts || '');
      setPreferredSpace(initialData.preferred_space || '');

      // Fetch facility resources if facility is selected
      if (initialData.facility?._id || initialData.facility) {
        fetchFacilityResources(initialData.facility?._id || initialData.facility);
      }
    }
  }, [mode, initialData, projectRoles]);

  // Initialize default experiment condition for new applications (after cache restoration)
  useEffect(() => {
    // Only run for new applications after cache has been processed
    if (mode !== 'edit' && !initialData && cacheRestored && experimentConditions.length === 0) {
      setExperimentConditions([{
        photoOnHour: 'N/A',
        photoOnMinute: 'N/A',
        photoOnPeriod: 'N/A',
        photoOffHour: 'N/A',
        photoOffMinute: 'N/A',
        photoOffPeriod: 'N/A',
        nightInterruptOnHour: 'N/A',
        nightInterruptOnMinute: 'N/A',
        nightInterruptOnPeriod: 'N/A',
        nightInterruptOffHour: 'N/A',
        nightInterruptOffMinute: 'N/A',
        nightInterruptOffPeriod: 'N/A',
        tempDay: '',
        tempNight: '',
        co2Day: '400',
        co2Night: '400',
        humidityDay: '50',
        humidityNight: '50'
      }]);
    }
  }, [mode, initialData, cacheRestored, experimentConditions.length]);

  useEffect(() => {
    const fetchUserDetails = async (email) => {
      try {
        const response = await axios.get(`/api/users/profile/by-email/${email}`);
        return response.data.is_ncsu;
      } catch (error) {
        console.error('Error fetching user details:', error);
        return false;
      }
    };

    const checkPIStatus = async () => {
      const piRole = projectRoles.find(r => r.role === 'PI');
      if (!piRole) return;
      const pi = personnel.find(p => p.role === piRole._id);
      if (pi?.email) {
        const isNCSU = await fetchUserDetails(pi.email);
        setIsPINCSU(isNCSU);
      }
    };

    if (personnel.length > 0) {
      checkPIStatus();
    }
  }, [personnel]);

  const handlePersonnelChange = async (index, field, value) => {
    const updatedPersonnel = [...personnel];
    updatedPersonnel[index][field] = value;
    
    // If the email field was changed and contains a valid email, check if user exists
    if (field === 'email' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      setIsCheckingEmail(true);
      try {
        const response = await axios.get(`/api/users/check-email/${value}`);
        if (response.data.exists) {
          // User exists, pre-fill data
          // Handle department - it could be an object (populated) or string (_id)
          let departmentValue = '';
          if (response.data.user.customDepartment) {
            departmentValue = 'not_listed';
          } else if (response.data.user.department) {
            departmentValue = typeof response.data.user.department === 'object' 
              ? response.data.user.department._id 
              : response.data.user.department;
          }
          
          updatedPersonnel[index] = {
            ...updatedPersonnel[index],
            firstName: response.data.user.first_name,
            lastName: response.data.user.last_name,
            department: departmentValue,
            customDepartment: response.data.user.customDepartment || '',
            existingUser: true,
            userId: response.data.user._id
          };
          toast.success(`User ${value} found and details added`);
        } else {
          // User doesn't exist, mark as non-existing
          updatedPersonnel[index] = {
            ...updatedPersonnel[index],
            existingUser: false,
            userId: null
          };
        }
      } catch (error) {
        console.error('Error checking email:', error);
      } finally {
        setIsCheckingEmail(false);
      }
    }
    
    setPersonnel(updatedPersonnel);
  };

  const addPersonnel = () => {
    setPersonnel([...personnel, { firstName: '', lastName: '', email: '', department: '', role: '', isDefault: false }]);
  };

  const handleExperimentConditionChange = (index, field, value) => {
    const updatedConditions = [...experimentConditions];
    
    // Handle N/A selection logic for all sections
    if (value === 'N/A') {
      // If selecting N/A for hour, also set minute and period to N/A for the same section
      if (field === 'photoOnHour') {
        updatedConditions[index] = {
          ...updatedConditions[index],
          photoOnHour: 'N/A',
          photoOnMinute: 'N/A',
          photoOnPeriod: 'N/A'
        };
      } else if (field === 'photoOffHour') {
        updatedConditions[index] = {
          ...updatedConditions[index],
          photoOffHour: 'N/A',
          photoOffMinute: 'N/A',
          photoOffPeriod: 'N/A'
        };
      } else if (field === 'nightInterruptOnHour') {
        updatedConditions[index] = {
          ...updatedConditions[index],
          nightInterruptOnHour: 'N/A',
          nightInterruptOnMinute: 'N/A',
          nightInterruptOnPeriod: 'N/A'
        };
      } else if (field === 'nightInterruptOffHour') {
        updatedConditions[index] = {
          ...updatedConditions[index],
          nightInterruptOffHour: 'N/A',
          nightInterruptOffMinute: 'N/A',
          nightInterruptOffPeriod: 'N/A'
        };
      } else {
        updatedConditions[index] = {
          ...updatedConditions[index],
          [field]: value
        };
      }
    } else {
      // If changing from N/A to a valid value, reset the section to default values
      if (field === 'photoOnHour' && updatedConditions[index].photoOnHour === 'N/A') {
        updatedConditions[index] = {
          ...updatedConditions[index],
          photoOnHour: value,
          photoOnMinute: '00',
          photoOnPeriod: 'AM'
        };
      } else if (field === 'photoOffHour' && updatedConditions[index].photoOffHour === 'N/A') {
        updatedConditions[index] = {
          ...updatedConditions[index],
          photoOffHour: value,
          photoOffMinute: '00',
          photoOffPeriod: 'AM'
        };
      } else if (field === 'nightInterruptOnHour' && updatedConditions[index].nightInterruptOnHour === 'N/A') {
        updatedConditions[index] = {
          ...updatedConditions[index],
          nightInterruptOnHour: value,
          nightInterruptOnMinute: '00',
          nightInterruptOnPeriod: 'AM'
        };
      } else if (field === 'nightInterruptOffHour' && updatedConditions[index].nightInterruptOffHour === 'N/A') {
        updatedConditions[index] = {
          ...updatedConditions[index],
          nightInterruptOffHour: value,
          nightInterruptOffMinute: '00',
          nightInterruptOffPeriod: 'AM'
        };
      } else {
        updatedConditions[index] = {
          ...updatedConditions[index],
          [field]: value
        };
      }
    }
    
    setExperimentConditions(updatedConditions);
  };

  const addExperimentCondition = () => {
    setExperimentConditions([...experimentConditions, {
      photoOnHour: 'N/A',
      photoOnMinute: 'N/A',
      photoOnPeriod: 'N/A',
      photoOffHour: 'N/A',
      photoOffMinute: 'N/A',
      photoOffPeriod: 'N/A',
      nightInterruptOnHour: 'N/A',
      nightInterruptOnMinute: 'N/A',
      nightInterruptOnPeriod: 'N/A',
      nightInterruptOffHour: 'N/A',
      nightInterruptOffMinute: 'N/A',
      nightInterruptOffPeriod: 'N/A',
      tempDay: '',
      tempNight: '',
      co2Day: '400',
      co2Night: '400',
      humidityDay: '50',
      humidityNight: '50'
    }]);
  };

  const removeExperimentCondition = (index) => {
    const updatedConditions = experimentConditions.filter((_, i) => i !== index);
    setExperimentConditions(updatedConditions);
  };

  const handleExperimentDetailsChange = (index, field, value) => {
    const updatedDetails = [...experimentDetails];
    updatedDetails[index][field] = value;
    setExperimentDetails(updatedDetails);
  };

  const addExperimentDetail = () => {
    setExperimentDetails([...experimentDetails, { water: '', substrate: '', species: '', speciesNotListed: '' }]);
  };

  const removeExperimentDetail = (index) => {
    const updatedDetails = experimentDetails.filter((_, i) => i !== index);
    setExperimentDetails(updatedDetails);
  };

  const fetchFacilityResources = async (facilityId) => {
    try {
      const [waterRes, substrateRes, speciesRes, potsRes] = await Promise.all([
        axios.get('/api/waters'),
        axios.get('/api/substrates'),
        axios.get('/api/species'),
        axios.get('/api/pots')
      ]);

      const filteredWaters = waterRes.data.filter(
        water => water.facility?._id === facilityId
      );
      const filteredSubstrates = substrateRes.data.filter(
        substrate => substrate.facility?._id === facilityId
      );
      const filteredSpecies = speciesRes.data.filter(
        species => species.facility?._id === facilityId
      );
      const filteredPots = potsRes.data.filter(
        pot => pot.facility?._id === facilityId
      );

      setWaterOptions(filteredWaters);
      setSubstrateOptions(filteredSubstrates);
      setSpeciesOptions(filteredSpecies);
      setPotOptions(filteredPots);
    } catch (error) {
      console.error('Error fetching facility resources:', error);
      setError('Failed to load facility resources');
    }
  };

  const handleFacilityChange = (e) => {
    const facilityId = e.target.value;
    setSelectedFacility(facilityId);
    
    // Clear all options if no facility is selected
    if (!facilityId) {
      setWaterOptions([]);
      setSubstrateOptions([]);
      setSpeciesOptions([]);
      setPotOptions([]);
      return;
    }
    
    fetchFacilityResources(facilityId);
  };

  const handleAccountNumberChange = (e) => {
    setAccountNumber(formatAccountNumber(e.target.value));
  };

  const handleSourceOfFundsChange = (e) => {
    setSourceOfFunds(e.target.value);
  };

  // Reset account form
  const resetAccountForm = () => {
    setSelectedUserForAccount('');
    setSelectedAccountNumber('');
    setAccountNumber('');
    setSourceOfFunds('');
    setIsNewAccount(false);
    setSelectedUserAccountNumbers([]);
    setShowAddAccountForm(false);
  };

  // Handle user selection for account
  const handleUserSelectionForAccount = async (email) => {
    setSelectedUserForAccount(email);
    setSelectedAccountNumber('');
    setAccountNumber('');
    setSourceOfFunds('');
    setIsNewAccount(false);
    
    if (!email) {
      setSelectedUserAccountNumbers([]);
      return;
    }
    
    try {
      setLoadingUserAccounts(true);
      const encodedEmail = encodeURIComponent(email);
      const response = await axios.get(`/api/users/with-accounts/${encodedEmail}`);
      setSelectedUserAccountNumbers(response.data.account_numbers || []);
    } catch (error) {
      console.error('Error fetching user account numbers:', error);
      // Set empty array instead of showing error - user might not have any accounts yet
      setSelectedUserAccountNumbers([]);
      // Don't show error toast - it's normal for users to not have account numbers yet
    } finally {
      setLoadingUserAccounts(false);
    }
  };

  // Handle account number selection
  const handleAccountNumberSelection = (accountId) => {
    setSelectedAccountNumber(accountId);
    
    if (accountId === 'new') {
      setIsNewAccount(true);
      setAccountNumber('');
      setSourceOfFunds('');
    } else if (accountId) {
      const selectedAccount = selectedUserAccountNumbers.find(acc => acc._id === accountId);
      if (selectedAccount) {
        setIsNewAccount(false);
        setAccountNumber(selectedAccount.number);
        setSourceOfFunds(selectedAccount.source);
      }
    } else {
      setIsNewAccount(false);
      setAccountNumber('');
      setSourceOfFunds('');
    }
  };

  const handleAddFunding = () => {
    if (!selectedUserForAccount) {
      toast.error('Please select a user first');
      return;
    }

    if (!accountNumber || !sourceOfFunds) {
      toast.error('Please fill in both account number and source of funds');
      return;
    }

    // Check for duplicates in the current funding list
    const isDuplicate = funding.some(f => 
      f.account_number === accountNumber && f.source_of_funds === sourceOfFunds
    );

    if (isDuplicate) {
      toast.error('This funding entry already exists');
      return;
    }

    const selectedUser = personnel.find(p => p.email === selectedUserForAccount);
    
    // Create funding entry
    const newFundingEntry = { 
      account_number: accountNumber, 
      source_of_funds: sourceOfFunds,
      user_email: selectedUserForAccount,
      user_name: selectedUser ? `${selectedUser.firstName || selectedUser.first_name} ${selectedUser.lastName || selectedUser.last_name}` : selectedUserForAccount,
      is_new_account: isNewAccount || selectedAccountNumber === 'new'
    };

    // If this is a new account, track it for saving later
    
    if (isNewAccount || selectedAccountNumber === 'new') {
      // Track new accounts for saving to user profiles
      const newAccountToSave = {
        userEmail: selectedUserForAccount,
        accountNumber: accountNumber,
        sourceOfFunds: sourceOfFunds
      };
      
      // Check if this exact account is already in the list to save
      const alreadyTracked = newAccountsToSave.some(acc => 
        acc.userEmail === selectedUserForAccount && acc.accountNumber === accountNumber
      );
      
      if (!alreadyTracked) {
        setNewAccountsToSave([...newAccountsToSave, newAccountToSave]);
      }
    }

    setFunding([...funding, newFundingEntry]);
    
    // Reset form completely
    setSelectedUserForAccount('');
    setAccountNumber('');
    setSourceOfFunds('');
    setSelectedAccountNumber('');
    setIsNewAccount(false);
    setSelectedUserAccountNumbers([]);
    setShowAddAccountForm(false);
    
    toast.success('Account added to project funding list');
  };

  const handleRemoveFunding = (index) => {
    const removedFunding = funding[index];
    
    // If removing a new account, also remove it from the accounts to save
    if (removedFunding.is_new_account) {
      setNewAccountsToSave(newAccountsToSave.filter(acc => 
        !(acc.accountNumber === removedFunding.account_number && 
          acc.userEmail === removedFunding.user_email)
      ));
    }
    
    setFunding(funding.filter((_, i) => i !== index));
  };

  // Note: saveNewAccountsToUsers function removed - account numbers are now handled automatically by the backend

  // Comprehensive form validation function
  const validateForm = (status) => {
    const errors = [];

    // For 'Saved' status, skip all validation - allow saving incomplete forms
    // Users can save their progress at any point and continue later
    if (status === 'Saved') {
      return errors; // Return empty errors array - no validation for saving
    }

    // Full validation only applies to 'Submitted' status

    // Basic required fields
    if (!projectTitle.trim()) {
      errors.push('Project Title is required');
    }
    
    if (!startDate) {
      errors.push('Project Start Date is required');
    }
    
    if (!selectedFacility) {
      errors.push('Facility selection is required');
    }

    // Date validation
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      if (start >= end) {
        errors.push('Project End Date must be after Start Date');
      }
    }

    // Personnel validation
    if (personnel.length === 0) {
      errors.push('At least one team member is required');
    } else {
      // Check for incomplete personnel entries
      personnel.forEach((person, index) => {
        const personNum = index + 1;
        if (!person.firstName?.trim()) {
          errors.push(`Team Member ${personNum}: First Name is required`);
        }
        if (!person.lastName?.trim()) {
          errors.push(`Team Member ${personNum}: Last Name is required`);
        }
        if (!person.email?.trim()) {
          errors.push(`Team Member ${personNum}: Email is required`);
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(person.email)) {
          errors.push(`Team Member ${personNum}: Valid email address is required`);
        }
        if (!person.department) {
          errors.push(`Team Member ${personNum}: Department is required`);
        } else if (person.department === 'not_listed' && !person.customDepartment?.trim()) {
          errors.push(`Team Member ${personNum}: Department name is required when "Department not listed" is selected`);
        }
        if (!person.role) {
          errors.push(`Team Member ${personNum}: Role is required`);
        }
      });

      // Every project must have a PI. A Co-PI alone is not sufficient.
      const piRole = projectRoles.find(r => r.role === 'PI');
      
      if (!piRole) {
        errors.push('System error: PI role not found. Please contact support.');
      } else {
        const hasPI = personnel.some(person => person.role === piRole._id);
        
        if (!hasPI) {
          errors.push('At least one team member must be assigned as PI (Principal Investigator)');
        }
      }
    }

    // Experiment Conditions validation
    if (experimentConditions.length === 0) {
      errors.push('At least one Experiment Condition is required');
    } else {
      experimentConditions.forEach((condition, index) => {
        const conditionNum = index + 1;
        // Validate Photo ON time (allow N/A or complete time)
        if (!condition.photoOnHour || !condition.photoOnMinute || !condition.photoOnPeriod) {
          errors.push(`Experiment Condition ${conditionNum}: Photo ON time is incomplete`);
        } else if (condition.photoOnHour !== 'N/A' && (condition.photoOnMinute === 'N/A' || condition.photoOnPeriod === 'N/A')) {
          errors.push(`Experiment Condition ${conditionNum}: Photo ON time is inconsistent (mix of N/A and time values)`);
        }
        
        // Validate Photo OFF time (allow N/A or complete time)
        if (!condition.photoOffHour || !condition.photoOffMinute || !condition.photoOffPeriod) {
          errors.push(`Experiment Condition ${conditionNum}: Photo OFF time is incomplete`);
        } else if (condition.photoOffHour !== 'N/A' && (condition.photoOffMinute === 'N/A' || condition.photoOffPeriod === 'N/A')) {
          errors.push(`Experiment Condition ${conditionNum}: Photo OFF time is inconsistent (mix of N/A and time values)`);
        }
        
        // Validate Night Interrupt ON time (allow N/A or complete time)
        if (!condition.nightInterruptOnHour || !condition.nightInterruptOnMinute || !condition.nightInterruptOnPeriod) {
          errors.push(`Experiment Condition ${conditionNum}: Night Interrupt ON time is incomplete`);
        } else if (condition.nightInterruptOnHour !== 'N/A' && (condition.nightInterruptOnMinute === 'N/A' || condition.nightInterruptOnPeriod === 'N/A')) {
          errors.push(`Experiment Condition ${conditionNum}: Night Interrupt ON time is inconsistent (mix of N/A and time values)`);
        }
        
        // Validate Night Interrupt OFF time (allow N/A or complete time)
        if (!condition.nightInterruptOffHour || !condition.nightInterruptOffMinute || !condition.nightInterruptOffPeriod) {
          errors.push(`Experiment Condition ${conditionNum}: Night Interrupt OFF time is incomplete`);
        } else if (condition.nightInterruptOffHour !== 'N/A' && (condition.nightInterruptOffMinute === 'N/A' || condition.nightInterruptOffPeriod === 'N/A')) {
          errors.push(`Experiment Condition ${conditionNum}: Night Interrupt OFF time is inconsistent (mix of N/A and time values)`);
        }
        if (!condition.tempDay) {
          errors.push(`Experiment Condition ${conditionNum}: Day Temperature is required`);
        }
        if (!condition.tempNight) {
          errors.push(`Experiment Condition ${conditionNum}: Night Temperature is required`);
        }
        if (!condition.co2Day) {
          errors.push(`Experiment Condition ${conditionNum}: Day CO2 level is required`);
        }
        if (!condition.co2Night) {
          errors.push(`Experiment Condition ${conditionNum}: Night CO2 level is required`);
        }
        if (!condition.humidityDay) {
          errors.push(`Experiment Condition ${conditionNum}: Day Humidity is required`);
        }
        if (!condition.humidityNight) {
          errors.push(`Experiment Condition ${conditionNum}: Night Humidity is required`);
        }
      });
    }

    // Experiment Details validation
    if (experimentDetails.length === 0) {
      errors.push('At least one Experiment Detail (Water/Substrate/Species) is required');
    } else {
      experimentDetails.forEach((detail, index) => {
        const detailNum = index + 1;
        let hasAnyField = false;
        
        if (detail.water) hasAnyField = true;
        if (detail.substrate) hasAnyField = true;
        if (detail.species && detail.species !== 'not_listed') hasAnyField = true;
        if (detail.species === 'not_listed' && detail.speciesNotListed?.trim()) hasAnyField = true;
        
        if (!hasAnyField) {
          errors.push(`Experiment Detail ${detailNum}: At least one field (Water, Substrate, or Species) must be selected`);
        }
        
        if (detail.species === 'not_listed' && !detail.speciesNotListed?.trim()) {
          errors.push(`Experiment Detail ${detailNum}: Species name is required when "Not Listed" is selected`);
        }
      });
    }

    // Required text fields for submission and revision saves
    if (status === 'Submitted' || status === 'Revision Required' || status === 'Revised') {
      if (!projectTimeline.trim()) {
        errors.push('Project Timeline is required for submission');
      }
      if (!experimentalDesignSummary.trim()) {
        errors.push('Experimental Design Summary is required for submission');
      }
      if (!safetyTrainingVerified) {
        errors.push('Safety Training/Biosafety Verification must be confirmed for submission');
      }
    }

    // Pot and space information - now optional
    // Removed validation for numberOfPots and potSize to make them optional

    // BUA Number validation - required when transgenic or pathogen is selected
    if ((usesTransgenicPlants || usesPathogen) && !buaNumber.trim()) {
      errors.push('BUA Number is required when using transgenic plants or pathogens');
    }

    // Funding validation - only required for NCSU users
    if (isPINCSU && funding.length === 0) {
      errors.push('NCSU users must provide at least one funding source');
    }
    
    // If funding sources are provided, validate their completeness
    if (funding.length > 0) {
      funding.forEach((fund, index) => {
        const fundNum = index + 1;
        if (!fund.account_number?.trim()) {
          errors.push(`Funding Source ${fundNum}: Account Number is required when funding source is added`);
        }
        if (!fund.source_of_funds?.trim()) {
          errors.push(`Funding Source ${fundNum}: Source of Funds is required when funding source is added`);
        }
      });
    }

    return errors;
  };

  // Handle adding custom department to system (admin only)
  const handleAddCustomDepartment = (customDeptName) => {
    setSelectedCustomDept(customDeptName);
    setAddDeptDialogOpen(true);
  };

  // Handle successful department creation
  const handleDepartmentCreated = (newDepartment) => {
    // Refresh departments list
    const fetchDepartments = async () => {
      try {
        const response = await axios.get('/api/departments');
        setDepartments(response.data);
        toast.success('Department added to system! You can now select it from the dropdown.');
      } catch (error) {
        console.error('Error refreshing departments:', error);
      }
    };
    fetchDepartments();
  };

  // Clear form and cache function (without confirmation)
  const clearFormAndCache = () => {
    // Clear all form fields to initial state
    setProjectTitle('');
    setStartDate('');
    setEndDate('');
    setPersonnel([]); // This will clear custom departments as well
    setExperimentConditions([]);
    setExperimentDetails([]);
    setSelectedFacility('');
    setProjectTimeline('');
    setExperimentalDesignSummary('');
    setSpecialRequirements('');
    setAdditionalComments('');
    setUsesTransgenicPlants(false);
    setUsesPathogen(false);
    setBuaNumber('');
    setSafetyTrainingVerified(false);
    setFunding([]);
    setAccountNumber('');
    setSourceOfFunds('');
    setSelectedUserForAccount('');
    setSelectedAccountNumber('');
    setIsNewAccount(false);
    setNewAccountsToSave([]);
    setIsPINCSU(false);
    setPots([{ pot_size: '', quantity: '' }]);
    setNumberOfCarts('');
    setPreferredSpace('');
    
    // Clear the cache
    clearCache();
    
    // Reset cache restoration flag to allow fresh start
    setCacheRestored(false);
    
  };

  // Clear form function with confirmation (for manual clear button)
  const handleClearForm = () => {
    const confirmClear = window.confirm(
      'Are you sure you want to clear all form data?\n\nThis will:\n• Reset all form fields\n• Clear saved progress from browser storage\n• Cannot be undone\n\nClick OK to proceed or Cancel to keep your data.'
    );
    
    if (confirmClear) {
      clearFormAndCache();
      
      // Show confirmation
      toast.success('Form cleared successfully!', {
        autoClose: 3000
      });
    }
  };

  // When a client saves an application that was sent back for revision, it
  // stays in "Revision Required" so admins can keep reviewing. Saving does
  // not resubmit. Admins approve or reject when they are satisfied.
  const editingStatus = mode === 'edit' ? initialData?.project_status : null;
  const isRevisionEdit = editingStatus === 'Revision Required' || editingStatus === 'Revised';
  const clientSubmitStatus = isRevisionEdit ? 'Revision Required' : 'Submitted';

  const handleSubmit = async (status) => {
    try {
      if (mode === 'edit' && onSaveConfirm) {
        const shouldProceed = await onSaveConfirm();
        if (!shouldProceed) return;
      }

      // Comprehensive form validation
      const validationErrors = validateForm(status);
      if (validationErrors.length > 0) {
        // Show the first few errors (to avoid overwhelming the user)
        const maxErrors = 3;
        const errorMessage = validationErrors.slice(0, maxErrors).join('\n');
        const remainingErrors = validationErrors.length - maxErrors;
        
        const fullMessage = remainingErrors > 0 
          ? `${errorMessage}\n\n...and ${remainingErrors} more issue${remainingErrors > 1 ? 's' : ''}. Please review the form carefully.`
          : errorMessage;
          
        toast.error(fullMessage, {
          autoClose: 8000, // Longer display time for detailed errors
          style: { whiteSpace: 'pre-line' } // Allow line breaks
        });
        return;
      }

      // Note: Account numbers are now automatically saved by the backend when creating users
      // No need to save them separately in the frontend

      const applicationData = {
        project_title: projectTitle,
        project_start_date: startDate,
        project_end_date: endDate,
        users: personnel.map(person => ({
          first_name: person.firstName || person.first_name,
          last_name: person.lastName || person.last_name,
          email: person.email,
          department: typeof person.department === 'object' ? person.department._id : person.department,
          customDepartment: person.department === 'not_listed' ? person.customDepartment : undefined,
          role: person.role,
          isDefault: person.isDefault || false,
          userId: person.userId || null,  // Store user ID if user exists
          existingUser: person.existingUser || false  // Mark if user already exists
        })),
        facility: selectedFacility,
        experiment_conditions: experimentConditions.map(condition => ({
          photo_on: condition.photoOnHour === 'N/A' ? 'N/A' : `${condition.photoOnHour}:${condition.photoOnMinute} ${condition.photoOnPeriod}`,
          photo_off: condition.photoOffHour === 'N/A' ? 'N/A' : `${condition.photoOffHour}:${condition.photoOffMinute} ${condition.photoOffPeriod}`,
          night_interrupt_on: condition.nightInterruptOnHour === 'N/A' ? 'N/A' : `${condition.nightInterruptOnHour}:${condition.nightInterruptOnMinute} ${condition.nightInterruptOnPeriod}`,
          night_interrupt_off: condition.nightInterruptOffHour === 'N/A' ? 'N/A' : `${condition.nightInterruptOffHour}:${condition.nightInterruptOffMinute} ${condition.nightInterruptOffPeriod}`,
          temp_day: condition.tempDay,
          temp_night: condition.tempNight,
          co2_day: condition.co2Day,
          co2_night: condition.co2Night,
          humidity_day: condition.humidityDay,
          humidity_night: condition.humidityNight
        })) || [],
        experiment_details: experimentDetails || [],
        pots: pots.filter(pot => pot.pot_size && pot.quantity).map(pot => ({
          pot_size: pot.pot_size,
          quantity: parseInt(pot.quantity) || 0
        })),
        uses_transgenic_plants: usesTransgenicPlants,
        uses_pathogen: usesPathogen,
        bua_number: buaNumber || undefined,
        project_timeline: projectTimeline,
        experimental_design_summary: experimentalDesignSummary,
        special_environmental_requirements: specialRequirements,
        additional_comments: additionalComments,
        safety_training_verified: safetyTrainingVerified,
        project_status: status,
        funding: funding
      };

      // Transform experiment_details before submission
      applicationData.experiment_details = experimentDetails.map(detail => ({
        water: detail.water || undefined,
        substrate: detail.substrate || undefined,
        species: detail.species === 'not_listed' ? undefined : detail.species || undefined,
        species_not_listed: detail.species === 'not_listed' ? detail.speciesNotListed : undefined
      })).filter(detail => detail.water || detail.substrate || detail.species || detail.species_not_listed);

      if (mode === 'edit' && applicationId) {
        await axios.put(`/api/applications/${applicationId}`, applicationData);
      } else {
        await axios.post('/api/applications', applicationData);
      }

      // Clear new accounts array since they've been saved
      setNewAccountsToSave([]);

      if (typeof onSaved === 'function') {
        toast.success(isRevisionEdit
          ? 'Changes saved. The application stays in Revision Required until an admin approves or rejects it.'
          : `Application ${status.toLowerCase()} successfully!`);
        onSaved();
        return;
      }
      
      // Clear form and cache after successful save or submit
      clearFormAndCache();
      
      // Show success message and handle navigation
      setShowSuccessDialog(true);
      setSuccessMessage(`Application ${status.toLowerCase()} successfully!`);
      
      // Hide dialog and reload page after 1.5 seconds
      setTimeout(() => {
        setShowSuccessDialog(false);
        // Reload the page to prevent duplicate submissions
        window.location.reload();
      }, 1500);

    } catch (err) {
      console.error('Error submitting application:', err);
      
      // Provide specific error messages based on the error response
      let errorMessage = 'Failed to submit application';
      
      if (err.response) {
        // Server responded with error status
        const { status, data } = err.response;
        
        switch (status) {
          case 400:
            // Handle specific 400 error cases
            if (data.error && data.error.includes('already exists')) {
              errorMessage = data.error;
            } else if (data.details && Array.isArray(data.details)) {
              // Show validation errors
              errorMessage = `Validation errors: ${data.details.join(', ')}`;
            } else {
              errorMessage = data.error || data.message || 'Invalid form data. Please check all required fields and try again.';
            }
            break;
          case 401:
            errorMessage = 'You are not authorized to submit applications. Please log in again.';
            break;
          case 403:
            errorMessage = 'You do not have permission to submit applications.';
            break;
          case 422:
            errorMessage = data.message || 'Form validation failed. Please check your input and try again.';
            break;
          case 500:
            errorMessage = 'Server error occurred. Please try again later or contact support.';
            break;
          default:
            errorMessage = data.message || `Server error (${status}). Please try again later.`;
        }
        
        // If server provides specific field errors, include them
        if (data.errors && Array.isArray(data.errors)) {
          const fieldErrors = data.errors.join('\n');
          errorMessage += `\n\nSpecific issues:\n${fieldErrors}`;
        }
      } else if (err.request) {
        // Network error
        errorMessage = 'Network error. Please check your internet connection and try again.';
      } else {
        // Other error
        errorMessage = err.message || 'An unexpected error occurred. Please try again.';
      }
      
      toast.error(errorMessage, {
        autoClose: 8000,
        style: { whiteSpace: 'pre-line' }
      });
    }
  };

  // Build the application payload for save - used by both handleAdminSave and onSaveConfirm
  const buildApplicationPayload = () => {
    const applicationData = {
      project_title: projectTitle,
      project_start_date: startDate,
      project_end_date: endDate,
      users: personnel.map(person => ({
        first_name: person.firstName || person.first_name,
        last_name: person.lastName || person.last_name,
        email: person.email,
        department: typeof person.department === 'object' ? person.department._id : person.department,
        customDepartment: person.department === 'not_listed' ? person.customDepartment : undefined,
        role: person.role,
        isDefault: person.isDefault || false,
        is_ncsu: person.is_ncsu
      })),
        facility: selectedFacility,
        experiment_conditions: experimentConditions.map(condition => ({
          photo_on: condition.photoOnHour === 'N/A' ? 'N/A' : `${condition.photoOnHour}:${condition.photoOnMinute} ${condition.photoOnPeriod}`,
          photo_off: condition.photoOffHour === 'N/A' ? 'N/A' : `${condition.photoOffHour}:${condition.photoOffMinute} ${condition.photoOffPeriod}`,
          night_interrupt_on: condition.nightInterruptOnHour === 'N/A' ? 'N/A' : `${condition.nightInterruptOnHour}:${condition.nightInterruptOnMinute} ${condition.nightInterruptOnPeriod}`,
          night_interrupt_off: condition.nightInterruptOffHour === 'N/A' ? 'N/A' : `${condition.nightInterruptOffHour}:${condition.nightInterruptOffMinute} ${condition.nightInterruptOffPeriod}`,
          temp_day: condition.tempDay,
          temp_night: condition.tempNight,
          co2_day: condition.co2Day,
          co2_night: condition.co2Night,
          humidity_day: condition.humidityDay,
          humidity_night: condition.humidityNight
        })) || [],
        experiment_details: experimentDetails || [],
        pots: pots.filter(pot => pot.pot_size && pot.quantity).map(pot => ({
          pot_size: pot.pot_size,
          quantity: parseInt(pot.quantity) || 0
        })),
        uses_transgenic_plants: usesTransgenicPlants,
        uses_pathogen: usesPathogen,
        bua_number: buaNumber || undefined,
        project_timeline: projectTimeline,
        experimental_design_summary: experimentalDesignSummary,
        special_environmental_requirements: specialRequirements,
        additional_comments: additionalComments,
        safety_training_verified: safetyTrainingVerified,
        funding: funding
      };

    // Transform experiment_details before submission
    applicationData.experiment_details = experimentDetails.map(detail => ({
      water: detail.water || undefined,
      substrate: detail.substrate || undefined,
      species: detail.species === 'not_listed' ? undefined : detail.species || undefined,
      species_not_listed: detail.species === 'not_listed' ? detail.speciesNotListed : undefined
    })).filter(detail => detail.water || detail.substrate || detail.species || detail.species_not_listed);

    return applicationData;
  };

  const handleAdminSave = async () => {
    try {
      const applicationData = buildApplicationPayload();
      const response = await axios.put(`/api/admin/applications/${applicationId}`, applicationData);
      
      toast.success('Changes saved successfully!', {
        position: "top-right",
        autoClose: 1500,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });

      if (onFormChange) {
        onFormChange(response.data);
      }

      // Redirect back to the applications list after a short delay
      setTimeout(() => {
        window.location.href = '/admin/application';
      }, 1500);
    } catch (err) {
      console.error('Error updating application:', err);
      toast.error('Failed to save changes. Please try again.', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  const handleCombinedChange = (regularChangeHandler, value) => {
    if (onFormChange) {
      onFormChange();
    }
    regularChangeHandler(value);
  };

  // Restore cached data when it becomes available (only once)
  useEffect(() => {
    if (!isCacheLoading && !cacheRestored && mode === 'new') {
      if (cachedData && Object.keys(cachedData).length > 0) {
        // Restore cached data
        setIsRestoringCache(true);
        
        // Restore all cached data
        if (cachedData.projectTitle) setProjectTitle(cachedData.projectTitle);
        if (cachedData.startDate) setStartDate(cachedData.startDate);
        if (cachedData.endDate) setEndDate(cachedData.endDate);
        if (cachedData.personnel && cachedData.personnel.length > 0) {
          // Ensure custom departments are properly restored
          const restoredPersonnel = cachedData.personnel.map(person => ({
            ...person,
            customDepartment: person.customDepartment || ''
          }));
          setPersonnel(restoredPersonnel);
        }
        if (cachedData.experimentConditions && cachedData.experimentConditions.length > 0) setExperimentConditions(cachedData.experimentConditions);
        if (cachedData.experimentDetails) setExperimentDetails(cachedData.experimentDetails);
        if (cachedData.selectedFacility) setSelectedFacility(cachedData.selectedFacility);
        if (cachedData.projectTimeline) setProjectTimeline(cachedData.projectTimeline);
        if (cachedData.experimentalDesignSummary) setExperimentalDesignSummary(cachedData.experimentalDesignSummary);
        if (cachedData.specialRequirements) setSpecialRequirements(cachedData.specialRequirements);
        if (cachedData.additionalComments) setAdditionalComments(cachedData.additionalComments);
        if (cachedData.usesTransgenicPlants !== undefined) setUsesTransgenicPlants(cachedData.usesTransgenicPlants);
        if (cachedData.usesPathogen !== undefined) setUsesPathogen(cachedData.usesPathogen);
        if (cachedData.buaNumber) setBuaNumber(cachedData.buaNumber);
        if (cachedData.safetyTrainingVerified !== undefined) setSafetyTrainingVerified(cachedData.safetyTrainingVerified);
        if (cachedData.funding) setFunding(cachedData.funding);
        if (cachedData.accountNumber) setAccountNumber(cachedData.accountNumber);
        if (cachedData.sourceOfFunds) setSourceOfFunds(cachedData.sourceOfFunds);
        if (cachedData.selectedUserForAccount) setSelectedUserForAccount(cachedData.selectedUserForAccount);
        if (cachedData.selectedAccountNumber) setSelectedAccountNumber(cachedData.selectedAccountNumber);
        if (cachedData.isNewAccount !== undefined) setIsNewAccount(cachedData.isNewAccount);
        if (cachedData.newAccountsToSave) setNewAccountsToSave(cachedData.newAccountsToSave);
        if (cachedData.isPINCSU !== undefined) setIsPINCSU(cachedData.isPINCSU);
        if (cachedData.pots) setPots(cachedData.pots);
        if (cachedData.numberOfCarts) setNumberOfCarts(cachedData.numberOfCarts);
        if (cachedData.preferredSpace) setPreferredSpace(cachedData.preferredSpace);
        
        setTimeout(() => setIsRestoringCache(false), 100); // Small delay to prevent immediate auto-save
      }
      
      // Mark cache as processed (whether there was data or not)
      setCacheRestored(true);
    }
  }, [isCacheLoading, cachedData, cacheRestored, mode]);

  // Fetch facility resources when facility is selected (including from cache restoration)
  useEffect(() => {
    if (selectedFacility && facilities.length > 0) {
      fetchFacilityResources(selectedFacility);
    }
  }, [selectedFacility, facilities]);

  // Auto-save form data to cache whenever form fields change (but not during restoration)
  useEffect(() => {
    if (!isCacheLoading && !isRestoringCache && mode === 'new' && cacheRestored) {
      const formData = {
        projectTitle,
        startDate,
        endDate,
        personnel,
        experimentConditions,
        experimentDetails,
        selectedFacility,
        projectTimeline,
        experimentalDesignSummary,
        specialRequirements,
        additionalComments,
        usesTransgenicPlants,
        usesPathogen,
        buaNumber,
        safetyTrainingVerified,
        funding,
        accountNumber,
        sourceOfFunds,
        selectedUserForAccount,
        selectedAccountNumber,
        isNewAccount,
        newAccountsToSave,
        isPINCSU,
        pots,
        numberOfCarts,
        preferredSpace
      };
      
      updateCache(formData);
    }
  }, [
    projectTitle, startDate, endDate, personnel, experimentConditions, experimentDetails,
    selectedFacility, projectTimeline, experimentalDesignSummary, specialRequirements,
    additionalComments, usesTransgenicPlants, usesPathogen, buaNumber, safetyTrainingVerified,
    funding, accountNumber, sourceOfFunds, selectedUserForAccount, selectedAccountNumber,
    isNewAccount, newAccountsToSave, isPINCSU, pots, numberOfCarts,
    preferredSpace, isCacheLoading, isRestoringCache, mode, cacheRestored, updateCache
  ]);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>{error}</div>;
  }

  return (
    <div className="new-application-container">
      {!hideFormHeading && (
        <div className="form-header">
          <Typography variant="h4" gutterBottom>
            {mode === 'new' ? 'New Application' : 'Review Application'}
          </Typography>
          {mode === 'new' && (
            <div className="form-controls">
              <div className="auto-save-indicator">
                {isSaving ? (
                  <span className="saving">💾 Saving...</span>
                ) : (
                  <span className="saved">✓ Auto-saved</span>
                )}
              </div>
              <button 
                type="button" 
                className="clear-form-btn"
                onClick={handleClearForm}
                title="Clear all form data"
              >
                🗑️ Clear Form
              </button>
            </div>
          )}
        </div>
      )}
      <form onSubmit={(e) => { 
        e.preventDefault(); 
        if (onSaveConfirm) {
          onSaveConfirm(buildApplicationPayload());
        } else if (isAdminReview) {
          handleAdminSave();
        } else {
          handleSubmit(clientSubmitStatus);
        }
      }} className="new-application-form">
        {isAdminReview && (
          <div className="form-actions" style={{ marginBottom: '16px' }}>
            <button type="button" onClick={handleAdminSave}>Save Changes</button>
          </div>
        )}
        <label htmlFor="projectTitle">Project Title:</label>
        <input
          type="text"
          id="projectTitle"
          value={projectTitle}
          onChange={(e) => handleCombinedChange(setProjectTitle, e.target.value)}
          required
        />

        <div className="date-facility-row">
          <div className="date-group">
            <label htmlFor="startDate">Start Date:</label>
            <input
              type="date"
              id="startDate"
              value={startDate}
              onChange={(e) => handleCombinedChange(setStartDate, e.target.value)}
              required
            />
          </div>
          <div className="date-group">
            <label htmlFor="endDate">End Date: <span style={{ fontWeight: 'normal', fontSize: '0.85em', color: '#666' }}>(optional)</span></label>
            <input
              type="date"
              id="endDate"
              value={endDate}
              onChange={(e) => handleCombinedChange(setEndDate, e.target.value)}
            />
          </div>
          <div className="facility-group">
            <label htmlFor="facility">Select Facility:</label>
            <select
              id="facility"
              value={selectedFacility || ''}
              onChange={handleFacilityChange}
              required
            >
              <option value="">Select a facility</option>
              {[...facilities].sort((a, b) => (a.facility_name || '').localeCompare(b.facility_name || '')).map((facility) => (
                <option key={facility._id} value={facility._id}>
                  {facility.facility_name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <h2>Please List all Personnel involved with Project.</h2>
        <div className="personnel-table">
          <table>
            <thead>
              <tr>
                <th>* First Name</th>
                <th>* Last Name</th>
                <th>* Email</th>
                <th>* Department</th>
                <th>* Role</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
            {personnel.map((person, index) => (
              <tr key={index} className={`${person.existingUser ? "existing-user" : person.existingUser === false ? "new-user" : ""} ${person.isDeleted ? "deleted-user" : ""}`}>
                <td>
                  <input
                    type="text"
                    value={person.firstName || ''}
                    onChange={(e) => {
                      handleCombinedChange(
                        (value) => handlePersonnelChange(index, 'firstName', value),
                        e.target.value
                      );
                    }}
                    required
                    disabled={person.existingUser || person.isDeleted}
                    style={person.isDeleted ? { color: '#856404', fontStyle: 'italic' } : {}}
                  />
                  {isCheckingEmail && person.email && index === personnel.length - 1 && (
                    <div className="loading-indicator">Checking...</div>
                  )}
                </td>
                <td>
                  <input
                    type="text"
                    value={person.lastName || ''}
                    onChange={(e) => {
                      handleCombinedChange(
                        (value) => handlePersonnelChange(index, 'lastName', value),
                        e.target.value
                      );
                    }}
                    required
                    disabled={person.existingUser || person.isDeleted}
                    style={person.isDeleted ? { color: '#856404', fontStyle: 'italic' } : {}}
                  />
                </td>
                <td>
                  <input
                    type="email"
                    value={person.email || ''}
                    onChange={(e) => {
                      handleCombinedChange(
                        (value) => handlePersonnelChange(index, 'email', value),
                        e.target.value
                      );
                    }}
                    required
                    disabled={person.isDeleted}
                    style={person.isDeleted ? { color: '#856404', fontStyle: 'italic' } : {}}
                  />
                  {person.isDeleted && <span className="user-status deleted" style={{ backgroundColor: '#fff3cd', color: '#856404', marginLeft: '5px', padding: '2px 6px', borderRadius: '3px', fontSize: '11px' }}>⚠ Deleted User</span>}
                  {!person.isDeleted && person.existingUser && <span className="user-status existing">✓ Existing User</span>}
                  {!person.isDeleted && person.existingUser === false && <span className="user-status new">New User</span>}
                </td>
                <td>
                  <select
                    value={person.department || ''}
                    onChange={(e) => {
                      handleCombinedChange(
                        (value) => handlePersonnelChange(index, 'department', value),
                        e.target.value
                      );
                    }}
                    required
                  >
                    <option value="">Select department</option>
                    <option value="not_listed">Department not listed</option>
                    {[...departments].sort((a, b) => (a.department_name || '').localeCompare(b.department_name || '')).map((dept) => (
                      <option key={dept._id} value={dept._id}>
                        {dept.department_name} ({dept.department_code})
                      </option>
                    ))}
                  </select>
                  {person.department === 'not_listed' && (
                    <div style={{ marginTop: '5px' }}>
                      <input
                        type="text"
                        placeholder="Enter department name"
                        value={person.customDepartment || ''}
                        onChange={(e) => {
                          handleCombinedChange(
                            (value) => handlePersonnelChange(index, 'customDepartment', value),
                            e.target.value
                          );
                        }}
                        style={{ width: '100%', marginBottom: '5px' }}
                        required
                      />
                      {isAdminReview && person.customDepartment && (
                        <button
                          type="button"
                          className="add-dept-btn"
                          onClick={() => handleAddCustomDepartment(person.customDepartment)}
                          title="Add this department to the system"
                        >
                          ➕ Add to System
                        </button>
                      )}
                    </div>
                  )}
                </td>
                <td>
                  <select
                    value={person.role || ''}
                    onChange={(e) => {
                      handleCombinedChange(
                        (value) => handlePersonnelChange(index, 'role', value),
                        e.target.value
                      );
                    }}
                    required
                  >
                    <option value="">Select Role</option>
                    {[...projectRoles].sort((a, b) => (a.role || '').localeCompare(b.role || '')).map((role) => (
                      <option key={role._id} value={role._id}>
                        {role.role}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <button 
                    type="button" 
                    onClick={() => {
                      // Check if this is the last person
                      if (personnel.length === 1) {
                        toast.error('Cannot remove the last person. Please add another person first.');
                        return;
                      }
                      
                      // Check if removing this person would leave no PI
                      const remainingPersonnel = personnel.filter((_, i) => i !== index);
                      const piRole = projectRoles.find(r => r.role === 'PI');
                      
                      const hasPI = remainingPersonnel.some(p => p.role === piRole?._id);
                      
                      if (!hasPI) {
                        toast.error('Cannot remove this person. At least one PI must remain in the project.');
                        return;
                      }
                      
                      const updatedPersonnel = personnel.filter((_, i) => i !== index);
                      setPersonnel(updatedPersonnel);
                      if (onFormChange) onFormChange();
                    }}
                    className="delete-personnel-btn"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            </tbody>
          </table>
          <button type="button" onClick={addPersonnel}>Add Personnel</button>
        </div>

        <h2>Experiment Conditions</h2>
        <div className="experiment-conditions-table">
          <table>
            <thead>
              <tr className="table-header">
                <th colSpan="2">Photo Period</th>
                <th colSpan="2">Night Interrupt</th>
                <th colSpan="2">Temperature</th>
                <th colSpan="2">CO2</th>
                <th colSpan="2">Humidity</th>
                <th rowSpan="2"></th>
              </tr>
              <tr className="table-header">
                <th>On</th>
                <th>Off</th>
                <th>On</th>
                <th>Off</th>
                <th>Day</th>
                <th>Night</th>
                <th>Day</th>
                <th>Night</th>
                <th>Day</th>
                <th>Night</th>
              </tr>
            </thead>
            <tbody>
              {experimentConditions.map((condition, index) => (
                <tr key={index}>
                  <td>
                    <div className="time-input">
                      <select
                        value={condition.photoOnHour || '01'}
                        onChange={(e) => handleExperimentConditionChange(index, 'photoOnHour', e.target.value)}
                      >
                        <option value="N/A">N/A</option>
                        {[...Array(12)].map((_, i) => (
                          <option key={i} value={String(i + 1).padStart(2, '0')}>
                            {String(i + 1).padStart(2, '0')}
                          </option>
                        ))}
                      </select>
                      :
                      <select
                        value={condition.photoOnMinute || '00'}
                        onChange={(e) => handleExperimentConditionChange(index, 'photoOnMinute', e.target.value)}
                        disabled={condition.photoOnHour === 'N/A'}
                      >
                        <option value="N/A">N/A</option>
                        {[...Array(60)].map((_, i) => (
                          <option key={i} value={String(i).padStart(2, '0')}>
                            {String(i).padStart(2, '0')}
                          </option>
                        ))}
                      </select>
                      <select
                        value={condition.photoOnPeriod || 'AM'}
                        onChange={(e) => handleExperimentConditionChange(index, 'photoOnPeriod', e.target.value)}
                        disabled={condition.photoOnHour === 'N/A'}
                      >
                        <option value="N/A">N/A</option>
                        <option value="AM">AM</option>
                        <option value="PM">PM</option>
                      </select>
                    </div>
                  </td>
                  <td>
                    <div className="time-input">
                      <select
                        value={condition.photoOffHour || 'N/A'}
                        onChange={(e) => handleExperimentConditionChange(index, 'photoOffHour', e.target.value)}
                      >
                        <option value="N/A">N/A</option>
                        {[...Array(12)].map((_, i) => (
                          <option key={i} value={String(i + 1).padStart(2, '0')}>{String(i + 1).padStart(2, '0')}</option>
                        ))}
                      </select>
                      :
                      <select
                        value={condition.photoOffMinute || 'N/A'}
                        onChange={(e) => handleExperimentConditionChange(index, 'photoOffMinute', e.target.value)}
                        disabled={condition.photoOffHour === 'N/A'}
                      >
                        <option value="N/A">N/A</option>
                        {[...Array(60)].map((_, i) => (
                          <option key={i} value={String(i).padStart(2, '0')}>{String(i).padStart(2, '0')}</option>
                        ))}
                      </select>
                      <select
                        value={condition.photoOffPeriod || 'N/A'}
                        onChange={(e) => handleExperimentConditionChange(index, 'photoOffPeriod', e.target.value)}
                        disabled={condition.photoOffHour === 'N/A'}
                      >
                        <option value="N/A">N/A</option>
                        <option value="AM">AM</option>
                        <option value="PM">PM</option>
                      </select>
                    </div>
                  </td>
                  <td>
                    <div className="time-input">
                      <select
                        value={condition.nightInterruptOnHour || 'N/A'}
                        onChange={(e) => handleExperimentConditionChange(index, 'nightInterruptOnHour', e.target.value)}
                      >
                        <option value="N/A">N/A</option>
                        {[...Array(12)].map((_, i) => (
                          <option key={i} value={String(i + 1).padStart(2, '0')}>{String(i + 1).padStart(2, '0')}</option>
                        ))}
                      </select>
                      :
                      <select
                        value={condition.nightInterruptOnMinute || 'N/A'}
                        onChange={(e) => handleExperimentConditionChange(index, 'nightInterruptOnMinute', e.target.value)}
                        disabled={condition.nightInterruptOnHour === 'N/A'}
                      >
                        <option value="N/A">N/A</option>
                        {[...Array(60)].map((_, i) => (
                          <option key={i} value={String(i).padStart(2, '0')}>{String(i).padStart(2, '0')}</option>
                        ))}
                      </select>
                      <select
                        value={condition.nightInterruptOnPeriod || 'N/A'}
                        onChange={(e) => handleExperimentConditionChange(index, 'nightInterruptOnPeriod', e.target.value)}
                        disabled={condition.nightInterruptOnHour === 'N/A'}
                      >
                        <option value="N/A">N/A</option>
                        <option value="AM">AM</option>
                        <option value="PM">PM</option>
                      </select>
                    </div>
                  </td>
                  <td>
                    <div className="time-input">
                      <select
                        value={condition.nightInterruptOffHour || 'N/A'}
                        onChange={(e) => handleExperimentConditionChange(index, 'nightInterruptOffHour', e.target.value)}
                      >
                        <option value="N/A">N/A</option>
                        {[...Array(12)].map((_, i) => (
                          <option key={i} value={String(i + 1).padStart(2, '0')}>{String(i + 1).padStart(2, '0')}</option>
                        ))}
                      </select>
                      :
                      <select
                        value={condition.nightInterruptOffMinute || 'N/A'}
                        onChange={(e) => handleExperimentConditionChange(index, 'nightInterruptOffMinute', e.target.value)}
                        disabled={condition.nightInterruptOffHour === 'N/A'}
                      >
                        <option value="N/A">N/A</option>
                        {[...Array(60)].map((_, i) => (
                          <option key={i} value={String(i).padStart(2, '0')}>{String(i).padStart(2, '0')}</option>
                        ))}
                      </select>
                      <select
                        value={condition.nightInterruptOffPeriod || 'N/A'}
                        onChange={(e) => handleExperimentConditionChange(index, 'nightInterruptOffPeriod', e.target.value)}
                        disabled={condition.nightInterruptOffHour === 'N/A'}
                      >
                        <option value="N/A">N/A</option>
                        <option value="AM">AM</option>
                        <option value="PM">PM</option>
                      </select>
                    </div>
                  </td>
                  <td>
                    <input
                      type="text"
                      value={condition.tempDay}
                      onChange={(e) => handleExperimentConditionChange(index, 'tempDay', e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={condition.tempNight}
                      onChange={(e) => handleExperimentConditionChange(index, 'tempNight', e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={condition.co2Day}
                      onChange={(e) => handleExperimentConditionChange(index, 'co2Day', e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={condition.co2Night}
                      onChange={(e) => handleExperimentConditionChange(index, 'co2Night', e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={condition.humidityDay}
                      onChange={(e) => handleExperimentConditionChange(index, 'humidityDay', e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={condition.humidityNight}
                      onChange={(e) => handleExperimentConditionChange(index, 'humidityNight', e.target.value)}
                    />
                  </td>
                  <td>
                    <button type="button" onClick={() => removeExperimentCondition(index)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button type="button" onClick={addExperimentCondition}>Add Experiment Condition</button>
        </div>

        <h2>Experiment Details</h2>
        <div className="experiment-details-table">
          {!selectedFacility ? (
            <div className="no-facility-message">
              Please select a facility first to view available resources
            </div>
          ) : (
            <table>
              <thead>
                <tr className="table-header">
                  <th>Species</th>
                  <th>Species Not Listed</th>
                  <th>Water</th>
                  <th>Substrate</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {experimentDetails.map((detail, index) => (
                  <tr key={index}>
                    <td>
                      <select
                        value={detail.species}
                        onChange={(e) => handleExperimentDetailsChange(index, 'species', e.target.value)}
                        required
                      >
                        <option value="">Select Species</option>
                        {[...speciesOptions].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((species) => (
                          <option key={species._id} value={species._id}>
                            {species.name} - {species.scientific_name}
                          </option>
                        ))}
                        <option value="not_listed">Species not listed</option>
                      </select>
                    </td>
                    <td>
                      <input
                        type="text"
                        value={detail.speciesNotListed}
                        onChange={(e) => handleExperimentDetailsChange(index, 'speciesNotListed', e.target.value)}
                        placeholder={detail.species === 'not_listed' ? 'Enter species name' : 'Only if species not listed'}
                        disabled={detail.species !== 'not_listed'}
                        required={detail.species === 'not_listed'}
                      />
                    </td>
                    <td>
                      <select
                        value={detail.water}
                        onChange={(e) => handleExperimentDetailsChange(index, 'water', e.target.value)}
                        required
                      >
                        <option value="">Select Water</option>
                        {[...waterOptions].sort((a, b) => (a.description || '').localeCompare(b.description || '')).map((water) => (
                          <option key={water._id} value={water._id}>
                            {water.description}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <select
                        value={detail.substrate}
                        onChange={(e) => handleExperimentDetailsChange(index, 'substrate', e.target.value)}
                        required
                      >
                        <option value="">Select Substrate</option>
                        {[...substrateOptions].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((substrate) => (
                          <option key={substrate._id} value={substrate._id}>
                            {substrate.name || substrate.description || 'Unknown'}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <button type="button" onClick={() => removeExperimentDetail(index)}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {selectedFacility && (
            <button type="button" onClick={addExperimentDetail}>
              Add Experiment Detail
            </button>
          )}
        </div>

        <div className="additional-experiment-details">
          <div className="form-section">
            <h4>Pot Requirements</h4>
            {pots.map((pot, index) => (
              <div key={index} className="form-row pot-row">
                <div className="form-group">
                  <label htmlFor={`potSize-${index}`}>Pot Size:</label>
                  <select
                    id={`potSize-${index}`}
                    value={pot.pot_size}
                    onChange={(e) => {
                      const newPots = [...pots];
                      newPots[index].pot_size = e.target.value;
                      handleCombinedChange(setPots, newPots);
                    }}
                  >
                    <option value="">Select Pot Size</option>
                    {[...potOptions].sort((a, b) => (a.pot || '').localeCompare(b.pot || '')).map((potOption) => (
                      <option key={potOption._id} value={potOption._id}>
                        {potOption.pot} - {potOption.pot_description}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor={`quantity-${index}`}>Quantity:</label>
                  <input
                    type="number"
                    id={`quantity-${index}`}
                    min="1"
                    value={pot.quantity}
                    onChange={(e) => {
                      const newPots = [...pots];
                      newPots[index].quantity = e.target.value;
                      handleCombinedChange(setPots, newPots);
                    }}
                  />
                </div>

                <div className="form-group pot-actions">
                  {pots.length > 1 && (
                    <button
                      type="button"
                      className="btn-remove-pot"
                      onClick={() => {
                        const newPots = pots.filter((_, i) => i !== index);
                        handleCombinedChange(setPots, newPots);
                      }}
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            ))}
            
            <button
              type="button"
              className="btn-add-pot"
              onClick={() => {
                const newPots = [...pots, { pot_size: '', quantity: '' }];
                handleCombinedChange(setPots, newPots);
              }}
            >
              + Add Another Pot Size
            </button>
          </div>

        </div>

        <div className="additional-fields">
          <div className="text-field">
            <label htmlFor="projectTimeline">* Project Timeline including harvest and data collection:</label>
            <textarea
              id="projectTimeline"
              value={projectTimeline}
              onChange={(e) => handleCombinedChange(setProjectTimeline, e.target.value)}
              required
            />
          </div>

          <div className="text-field">
            <label htmlFor="experimentalDesign">
              * Summary of experimental design including an explanation of cultural procedures and/or any special techniques
              (limit to 250 words):
            </label>
            <textarea
              id="experimentalDesign"
              value={experimentalDesignSummary}
              onChange={(e) => handleCombinedChange(setExperimentalDesignSummary, e.target.value)}
              maxLength={2000}
              required
            />
          </div>

          <div className="text-field">
            <label htmlFor="specialRequirements">Special Environmental Requirements (optional):</label>
            <textarea
              id="specialRequirements"
              value={specialRequirements}
              onChange={(e) => handleCombinedChange(setSpecialRequirements, e.target.value)}
            />
          </div>

          <div className="text-field">
            <label htmlFor="additionalComments">Additional Comments (limit to 250 words):</label>
            <textarea
              id="additionalComments"
              value={additionalComments}
              onChange={(e) => handleCombinedChange(setAdditionalComments, e.target.value)}
              maxLength={2000}
            />
          </div>

          <div className="checkbox-group">
            <div className="checkbox-field">
              <input
                type="checkbox"
                id="transgenicPlants"
                checked={usesTransgenicPlants}
                onChange={(e) => handleCombinedChange(setUsesTransgenicPlants, e.target.checked)}
              />
              <label htmlFor="transgenicPlants">Research uses transgenic plants</label>
            </div>

            <div className="checkbox-field">
              <input
                type="checkbox"
                id="pathogen"
                checked={usesPathogen}
                onChange={(e) => handleCombinedChange(setUsesPathogen, e.target.checked)}
              />
              <label htmlFor="pathogen">Research uses pathogen</label>
            </div>

            {/* BUA Number field - only shown when transgenic OR pathogen is selected */}
            {(usesTransgenicPlants || usesPathogen) && (
              <div className="bua-number-field">
                <label htmlFor="buaNumber">* BUA Number:</label>
                <input
                  type="text"
                  id="buaNumber"
                  value={buaNumber}
                  onChange={(e) => handleCombinedChange(setBuaNumber, e.target.value)}
                  placeholder="Enter BUA number"
                  required
                />
              </div>
            )}
          </div>

          <div className="safety-verification">
            <div className="checkbox-field">
              <input
                type="checkbox"
                id="safetyTraining"
                checked={safetyTrainingVerified}
                onChange={(e) => handleCombinedChange(setSafetyTrainingVerified, e.target.checked)}
                required
              />
              <label htmlFor="safetyTraining">
                * Safety Training/Biosafety Verification
              </label>
            </div>
            <p className="safety-text">
              I certify that all personnel associated with our research project being conducted in the North Campus State University Phytotron and Plant Science Building, including technical staff, graduate students, undergraduate student assistants, postdoctoral associates and visiting research scientists, have received proper training in standard operating procedures of equipment, in proper handling of any chemicals involved with the research, and in the rules and regulations regarding laboratory safety, and all necessary information has been disclosed on the Transgenic Plant Research Biosafety Verification page of this proposal.
            </p>
          </div>

          {/* Account Information Section */}
          <div className="section">
            <h2>Account Information</h2>
            {funding.map((fund, index) => (
              <div key={index} className="funding-entry" style={{ marginBottom: '10px', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div><strong>User:</strong> {fund.user_name || fund.user_email}</div>
                  <div><strong>Account Number:</strong> {displayAccountNumber(fund.account_number)}</div>
                  <div><strong>Source of Funds:</strong> {fund.source_of_funds}</div>
                </div>
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => handleRemoveFunding(index)}
                >
                  Remove
                </Button>
              </div>
            ))}

            {!showAddAccountForm ? (
              <div style={{ marginTop: '16px', textAlign: 'center' }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setShowAddAccountForm(true)}
                  style={{ height: '40px', fontSize: '16px' }}
                >
                  + Add Account Information
                </Button>
              </div>
            ) : (
              <div style={{ marginTop: '16px', padding: '16px', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#fafafa' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <h3 style={{ margin: 0 }}>Add Account Information</h3>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={resetAccountForm}
                    style={{ minWidth: 'auto' }}
                  >
                    Cancel
                  </Button>
                </div>
                
                {/* User Selection */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                    Select User:
                  </label>
                  <select
                    value={selectedUserForAccount}
                    onChange={(e) => handleUserSelectionForAccount(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      fontSize: '16px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      height: '40px'
                    }}
                  >
                    <option value="">Select a user from the personnel list</option>
                    {personnel.map((person, index) => (
                      <option key={index} value={person.email}>
                        {person.firstName || person.first_name} {person.lastName || person.last_name} ({person.email})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Account Number Selection */}
                {selectedUserForAccount && (
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                      Account Number:
                    </label>
                    {loadingUserAccounts ? (
                      <div style={{ padding: '8px', fontStyle: 'italic' }}>Loading account numbers...</div>
                    ) : (
                      <select
                        value={selectedAccountNumber}
                        onChange={(e) => handleAccountNumberSelection(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          fontSize: '16px',
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          height: '40px'
                        }}
                      >
                        <option value="">Select an account number</option>
                        {selectedUserAccountNumbers.map((account) => (
                          <option key={account._id} value={account._id}>
                            {account.number} - {account.source}
                          </option>
                        ))}
                        <option value="new">+ Add New Account Number</option>
                      </select>
                    )}
                  </div>
                )}

                {/* Account Details Form */}
                {(selectedUserForAccount && (isNewAccount || selectedAccountNumber === 'new' || selectedAccountNumber)) && (
                  <>
                    <div style={{ display: 'flex', gap: '16px', alignItems: 'center', marginBottom: '16px' }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                          Account Number:
                        </label>
                        <input
                          type="text"
                          placeholder="XXXXXX-XXXXX"
                          value={accountNumber}
                          onChange={handleAccountNumberChange}
                          disabled={!isNewAccount && selectedAccountNumber !== 'new'}
                          maxLength={12}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            fontSize: '16px',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            height: '40px',
                            backgroundColor: (!isNewAccount && selectedAccountNumber !== 'new') ? '#f5f5f5' : 'white'
                          }}
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                          Source of Funds:
                        </label>
                        <input
                          type="text"
                          placeholder="Enter source of funds"
                          value={sourceOfFunds}
                          onChange={handleSourceOfFundsChange}
                          disabled={!isNewAccount && selectedAccountNumber !== 'new'}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            fontSize: '16px',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            height: '40px',
                            backgroundColor: (!isNewAccount && selectedAccountNumber !== 'new') ? '#f5f5f5' : 'white'
                          }}
                        />
                      </div>
                    </div>

                    <Button
                      variant="contained"
                      onClick={handleAddFunding}
                      disabled={!accountNumber || !sourceOfFunds}
                      style={{ height: '40px' }}
                    >
                      {isNewAccount || selectedAccountNumber === 'new' ? 'Add New Account to Project' : 'Add to Project'}
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Debug info */}
        {newAccountsToSave.length > 0 && (
          <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#fff3cd', borderRadius: '4px', border: '1px solid #ffc107' }}>
            <div style={{ color: '#856404', fontSize: '14px', fontWeight: 'bold' }}>
              🔧 Debug: {newAccountsToSave.length} account(s) to save on submission
            </div>
            {newAccountsToSave.map((acc, idx) => (
              <div key={idx} style={{ fontSize: '12px', color: '#856404', marginTop: '4px' }}>
                • {acc.accountNumber} for {acc.userEmail} (source: {acc.sourceOfFunds})
              </div>
            ))}
          </div>
        )}

        <div className="form-actions">
          {isAdminReview ? (
            <button type="button" onClick={handleAdminSave}>Save Changes</button>
          ) : isRevisionEdit ? (
            <button type="submit">Save Changes</button>
          ) : (
            <>
              <button type="button" onClick={() => handleSubmit('Saved')}>Save Application</button>
              <button type="submit">Submit Application</button>
            </>
          )}
        </div>
      </form>
      {showSuccessDialog && (
        <div className="success-dialog">
          <div className="success-content">
            <div className="success-icon">✓</div>
            <p>{successMessage}</p>
          </div>
        </div>
      )}

      {/* Add Department Dialog for Admins */}
      <AddDepartmentDialog
        open={addDeptDialogOpen}
        onClose={() => setAddDeptDialogOpen(false)}
        onSuccess={handleDepartmentCreated}
        prefilledName={selectedCustomDept}
        title="Add Department to System"
      />
    </div>
  );
};

export default NewApplication;